import * as Sentry from "@sentry/react";

// Export Sentry instance for direct access
export { Sentry };

// Check if Sentry is properly initialized
const isSentryInitialized = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any).__SENTRY_INITIALIZED__ === true;
};

// Fallback logger for when Sentry is blocked
const fallbackLogger = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trace: (message: string, extra?: any) => console.log(`[TRACE] ${message}`, extra),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  debug: (message: string, extra?: any) => console.log(`[DEBUG] ${message}`, extra),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  info: (message: string, extra?: any) => console.info(`[INFO] ${message}`, extra),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  warn: (message: string, extra?: any) => console.warn(`[WARN] ${message}`, extra),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: (message: string, extra?: any) => console.error(`[ERROR] ${message}`, extra),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fatal: (message: string, extra?: any) => console.error(`[FATAL] ${message}`, extra),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fmt: (strings: TemplateStringsArray, ...values: any[]) => {
    return strings.reduce((result, str, i) => {
      return result + str + (values[i] || '');
    }, '');
  },
};

// Logger instance for structured logging with fallback.
//
// Lazy dispatcher: each method re-checks isSentryInitialized() at call time
// (mirroring sentryHelpers) rather than binding once at module load. Sentry
// finishes initializing after this module is first imported, so a module-load
// ternary would freeze every logger.* call to the console fallback and never
// upgrade — silently dropping all 27 structured logs in production. Resolving the
// target per invocation lets logs emitted after init reach Sentry's logging API.
export const logger = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trace: (message: string, extra?: any) =>
    isSentryInitialized() ? Sentry.logger.trace(message, extra) : fallbackLogger.trace(message, extra),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  debug: (message: string, extra?: any) =>
    isSentryInitialized() ? Sentry.logger.debug(message, extra) : fallbackLogger.debug(message, extra),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  info: (message: string, extra?: any) =>
    isSentryInitialized() ? Sentry.logger.info(message, extra) : fallbackLogger.info(message, extra),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  warn: (message: string, extra?: any) =>
    isSentryInitialized() ? Sentry.logger.warn(message, extra) : fallbackLogger.warn(message, extra),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: (message: string, extra?: any) =>
    isSentryInitialized() ? Sentry.logger.error(message, extra) : fallbackLogger.error(message, extra),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fatal: (message: string, extra?: any) =>
    isSentryInitialized() ? Sentry.logger.fatal(message, extra) : fallbackLogger.fatal(message, extra),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fmt: (strings: TemplateStringsArray, ...values: any[]) =>
    isSentryInitialized() ? Sentry.logger.fmt(strings, ...values) : fallbackLogger.fmt(strings, ...values),
};

// Helper functions for common Sentry operations
export const sentryHelpers = {
  /**
   * Check if Sentry is available and initialized
   */
  isAvailable: isSentryInitialized,

  /**
   * Capture an exception with context
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  captureException: (error: Error, context?: Record<string, any>) => {
    if (isSentryInitialized()) {
      Sentry.withScope((scope) => {
        if (context) {
          Object.entries(context).forEach(([key, value]) => {
            scope.setContext(key, value);
          });
        }
        Sentry.captureException(error);
      });
    } else {
      // Fallback to console when Sentry is blocked
      console.error("🚨 Error captured (Sentry unavailable):", error);
      if (context) {
        console.error("Context:", context);
      }
    }
  },

  /**
   * Capture a message with level and context
   */
  captureMessage: (
    message: string, 
    level: Sentry.SeverityLevel = "info", 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    context?: Record<string, any>
  ) => {
    if (isSentryInitialized()) {
      Sentry.withScope((scope) => {
        if (context) {
          Object.entries(context).forEach(([key, value]) => {
            scope.setContext(key, value);
          });
        }
        Sentry.captureMessage(message, level);
      });
    } else {
      // Fallback to console when Sentry is blocked
      const levelMap: Record<string, typeof console.log> = {
        debug: console.debug,
        info: console.info,
        warning: console.warn,
        error: console.error,
        fatal: console.error,
        log: console.log,
      };
      const logFn = levelMap[level] || console.log;
      logFn(`📝 [${level.toUpperCase()}] ${message}`, context || {});
    }
  },

  /**
   * Start a custom span for performance tracking
   */
  startSpan: <T>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    spanOptions: { op: string; name: string; attributes?: Record<string, any> },
    callback: (span: Sentry.Span) => T
  ): T => {
    if (isSentryInitialized()) {
      return Sentry.startSpan(
        {
          op: spanOptions.op,
          name: spanOptions.name,
          attributes: spanOptions.attributes,
        },
        callback
      );
    } else {
      // Fallback: just execute the callback without span tracking
      const mockSpan = {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setAttribute: (key: string, value: any) => {
          console.debug(`[SPAN] ${spanOptions.name} - ${key}:`, value);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any;
      return callback(mockSpan);
    }
  },

  /**
   * Set user context
   */
  setUser: (user: {
    id?: string;
    email?: string;
    username?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  }) => {
    if (isSentryInitialized()) {
      Sentry.setUser(user);
    } else {
      console.debug("👤 User context (Sentry unavailable):", user);
    }
  },

  /**
   * Add breadcrumb for debugging
   */
  addBreadcrumb: (
    message: string, 
    category?: string, 
    level?: Sentry.SeverityLevel,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: Record<string, any>
  ) => {
    if (isSentryInitialized()) {
      Sentry.addBreadcrumb({
        message,
        category: category || "custom",
        level: level || "info",
        data,
      });
    } else {
      console.debug(`🍞 [${category || "custom"}] ${message}`, data || {});
    }
  },
};

// React Error Boundary component
export const SentryErrorBoundary = Sentry.ErrorBoundary; 